import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  ArrowLeft, Building2, Wrench, Package, Boxes, Plus, Camera, Truck, Users,
  Receipt as ReceiptIcon, FileText, DollarSign, TrendingUp, X, CheckCircle2,
  Calendar, ImageIcon,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const JOB_TYPES = [
  { id: "steel_erection", label: "Steel Erection", icon: Wrench, desc: "Prep + install of structural steel" },
  { id: "panel_install", label: "Panel Install", icon: Package, desc: "Prep + install of precast/tilt panels" },
  { id: "fab_and_erect", label: "Fab & Erect Package", icon: Boxes, desc: "Prep + fabricate + install" },
  { id: "whole_build", label: "Whole Build", icon: Building2, desc: "End-to-end build management" },
];

const SUBBIE_ROLES = [
  { id: "fabricator", label: "Fabricator" },
  { id: "transport", label: "Transport" },
  { id: "paint", label: "Paint" },
  { id: "crane_crew", label: "Crane Crew" },
  { id: "rigging_crew", label: "Rigging Crew" },
  { id: "in_house", label: "In-House" },
  { id: "other", label: "Other" },
];

const fileToBase64 = (file) => new Promise((resolve, reject) => {
  const r = new FileReader();
  r.onload = () => resolve(r.result);
  r.onerror = reject;
  r.readAsDataURL(file);
});

const BuildWorkflowPage = () => {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const [job, setJob] = useState(null);
  const [machines, setMachines] = useState([]);
  const [budget, setBudget] = useState(null);
  const [receipts, setReceipts] = useState([]);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("setup");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [j, m, b, r, rep] = await Promise.all([
        axios.get(`${API}/jobs/${jobId}`),
        axios.get(`${API}/machines`),
        axios.get(`${API}/jobs/${jobId}/budget`).catch(() => ({ data: null })),
        axios.get(`${API}/jobs/${jobId}/receipts`).catch(() => ({ data: [] })),
        axios.get(`${API}/jobs/${jobId}/report`).catch(() => ({ data: null })),
      ]);
      setJob(j.data);
      setMachines(m.data || []);
      setBudget(b.data);
      setReceipts(r.data || []);
      setReport(rep.data);
      if (!j.data?.job_type) setTab("setup");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load job");
    } finally { setLoading(false); }
  }, [jobId]);

  useEffect(() => { load(); }, [load]);

  if (loading) return <div className="p-6 text-slate-400">Loading…</div>;
  if (!job) return <div className="p-6 text-slate-400">Job not found</div>;

  const isFab = job.job_type === "fab_and_erect" || job.job_type === "whole_build";

  return (
    <div className="space-y-4" data-testid="build-workflow-page">
      <div className="flex items-center gap-2">
        <button onClick={() => navigate(`/jobs/${jobId}`)} className="text-slate-500 hover:text-slate-900"><ArrowLeft className="w-5 h-5" /></button>
        <div>
          <h1 className="text-2xl font-bold font-heading">{job.title}</h1>
          <p className="text-xs text-slate-500">{job.client_name || "—"} · {job.location || "—"} · <span className="uppercase font-semibold">{job.job_type?.replace(/_/g, " ") || "Not set up"}</span></p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b-2 border-slate-200 overflow-x-auto">
        {[
          { id: "setup", label: "Setup" },
          { id: "progress", label: "Progress" },
          { id: "subbies", label: "Subbies" },
          ...(isFab ? [{ id: "deliveries", label: "Deliveries" }] : []),
          { id: "payments", label: "Progress Payments" },
          { id: "receipts", label: "Receipts" },
          { id: "report", label: "Report / Budget" },
        ].map(t => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={`px-4 py-2 text-xs font-bold uppercase tracking-wide whitespace-nowrap border-b-2 -mb-0.5 ${tab === t.id ? "border-orange-500 text-slate-900" : "border-transparent text-slate-500 hover:text-slate-700"}`}
            data-testid={`build-tab-${t.id}`}
          >{t.label}</button>
        ))}
      </div>

      {tab === "setup" && <SetupTab job={job} machines={machines} onSaved={load} />}
      {tab === "progress" && <ProgressTab job={job} onSaved={load} />}
      {tab === "subbies" && <SubbiesTab job={job} onSaved={load} />}
      {tab === "deliveries" && isFab && <DeliveriesTab job={job} onSaved={load} />}
      {tab === "payments" && <PaymentsTab job={job} onSaved={load} />}
      {tab === "receipts" && <ReceiptsTab jobId={jobId} receipts={receipts} onSaved={load} />}
      {tab === "report" && <ReportTab job={job} budget={budget} receipts={receipts} report={report} />}
    </div>
  );
};

// ============================================================
const SetupTab = ({ job, machines, onSaved }) => {
  const [f, setF] = useState({
    job_type: job.job_type || "",
    builder_name: job.builder_name || "",
    fabricator_name: job.fabricator_name || "",
    tonnage: job.tonnage || "",
    job_price_aud: job.job_price_aud || job.contract_amount || "",
    primary_crane_id: job.primary_crane_id || "",
    crane_ownership: job.crane_ownership || "owned",
    additional_equipment_ids: job.additional_equipment_ids || [],
    scope_notes: job.scope_notes || "",
  });
  const [saving, setSaving] = useState(false);

  const toggleEquip = (id) => setF(x => ({
    ...x,
    additional_equipment_ids: x.additional_equipment_ids.includes(id)
      ? x.additional_equipment_ids.filter(m => m !== id)
      : [...x.additional_equipment_ids, id],
  }));

  const save = async () => {
    if (!f.job_type) return toast.error("Pick a job type first");
    setSaving(true);
    try {
      await axios.post(`${API}/jobs/${job.id}/setup-build`, {
        ...f,
        tonnage: f.tonnage ? Number(f.tonnage) : null,
        job_price_aud: f.job_price_aud ? Number(f.job_price_aud) : null,
      });
      toast.success("Build workflow saved");
      await onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-4 max-w-3xl">
      <div>
        <label className="block text-xs font-bold uppercase text-slate-500 mb-2">Job Type</label>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          {JOB_TYPES.map(t => {
            const Icon = t.icon;
            const sel = f.job_type === t.id;
            return (
              <button key={t.id} onClick={() => setF({...f, job_type: t.id})}
                className={`p-3 border-2 rounded text-left ${sel ? "border-orange-500 bg-orange-50" : "border-slate-200 hover:border-slate-400"}`}
                data-testid={`job-type-${t.id}`}>
                <Icon className="w-5 h-5 mb-1 text-orange-500" />
                <div className="font-bold text-sm">{t.label}</div>
                <div className="text-[10px] text-slate-500 mt-0.5">{t.desc}</div>
              </button>
            );
          })}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div><label className="text-xs font-bold uppercase text-slate-500">Builder</label><input value={f.builder_name} onChange={e => setF({...f, builder_name: e.target.value})} className="w-full border-2 border-slate-200 rounded px-3 py-2" data-testid="setup-builder" /></div>
        <div><label className="text-xs font-bold uppercase text-slate-500">Fabricator</label><input value={f.fabricator_name} onChange={e => setF({...f, fabricator_name: e.target.value})} className="w-full border-2 border-slate-200 rounded px-3 py-2" data-testid="setup-fabricator" /></div>
        <div><label className="text-xs font-bold uppercase text-slate-500">Tonnage</label><input type="number" step="0.01" value={f.tonnage} onChange={e => setF({...f, tonnage: e.target.value})} className="w-full border-2 border-slate-200 rounded px-3 py-2" data-testid="setup-tonnage" /></div>
        <div><label className="text-xs font-bold uppercase text-slate-500">Job Price (AUD)</label><input type="number" step="0.01" value={f.job_price_aud} onChange={e => setF({...f, job_price_aud: e.target.value})} className="w-full border-2 border-slate-200 rounded px-3 py-2" data-testid="setup-price" /></div>
      </div>

      <div>
        <label className="text-xs font-bold uppercase text-slate-500">Primary Crane</label>
        <div className="flex gap-2">
          <select value={f.primary_crane_id} onChange={e => setF({...f, primary_crane_id: e.target.value})} className="flex-1 border-2 border-slate-200 rounded px-3 py-2" data-testid="setup-crane">
            <option value="">-- None --</option>
            {machines.map(m => <option key={m.id} value={m.id}>{m.registration_number || m.name} ({m.machine_type || m.type})</option>)}
          </select>
          <select value={f.crane_ownership} onChange={e => setF({...f, crane_ownership: e.target.value})} className="border-2 border-slate-200 rounded px-3 py-2" data-testid="setup-crane-ownership">
            <option value="owned">Owned</option>
            <option value="hired">Hired</option>
          </select>
        </div>
      </div>

      <div>
        <label className="text-xs font-bold uppercase text-slate-500 mb-1 block">Additional Equipment (tick all needed on site)</label>
        <div className="border-2 border-slate-200 rounded p-2 max-h-48 overflow-y-auto space-y-1">
          {machines.filter(m => m.id !== f.primary_crane_id).map(m => (
            <label key={m.id} className="flex items-center gap-2 text-sm cursor-pointer py-1 px-1 hover:bg-slate-50 rounded" data-testid={`setup-equip-${m.id}`}>
              <input type="checkbox" checked={f.additional_equipment_ids.includes(m.id)} onChange={() => toggleEquip(m.id)} className="w-4 h-4" />
              <span>{m.registration_number || m.name} <span className="text-slate-400">({m.machine_type || m.type})</span></span>
            </label>
          ))}
          {machines.length === 0 && <div className="text-xs text-slate-400 italic px-2">No machines yet</div>}
        </div>
      </div>

      <textarea value={f.scope_notes} onChange={e => setF({...f, scope_notes: e.target.value})} rows={3} placeholder="Scope notes / special requirements…" className="w-full border-2 border-slate-200 rounded px-3 py-2" />

      <button onClick={save} disabled={saving} className="px-5 py-3 bg-orange-500 hover:bg-orange-600 disabled:opacity-50 text-white rounded font-bold" data-testid="setup-save">
        {saving ? "Saving…" : "Save Setup"}
      </button>
    </div>
  );
};

// ============================================================
const ProgressTab = ({ job, onSaved }) => {
  const [show, setShow] = useState(false);
  const [f, setF] = useState({ milestone: "", notes: "", percent_complete: "", photo_urls: [] });
  const [uploading, setUploading] = useState(false);

  const addPhoto = async (e) => {
    const files = Array.from(e.target.files || []);
    const urls = [];
    for (const file of files) urls.push(await fileToBase64(file));
    setF(x => ({ ...x, photo_urls: [...x.photo_urls, ...urls] }));
  };

  const submit = async () => {
    if (!f.milestone.trim()) return toast.error("Milestone label required");
    setUploading(true);
    try {
      await axios.post(`${API}/jobs/${job.id}/progress`, {
        milestone: f.milestone,
        notes: f.notes,
        percent_complete: f.percent_complete ? Number(f.percent_complete) : null,
        photo_urls: f.photo_urls,
      });
      toast.success("Progress update added");
      setF({ milestone: "", notes: "", percent_complete: "", photo_urls: [] });
      setShow(false);
      await onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setUploading(false); }
  };

  const updates = (job.progress_updates || []).slice().reverse();

  return (
    <div className="space-y-4">
      <button onClick={() => setShow(true)} className="inline-flex gap-2 px-4 py-2 bg-orange-500 hover:bg-orange-600 text-white rounded font-semibold text-sm" data-testid="add-progress-btn"><Plus className="w-4 h-4" /> New Progress Update</button>
      {updates.length === 0 ? (
        <div className="text-center py-8 text-slate-400 italic">No progress updates yet.</div>
      ) : updates.map(u => (
        <div key={u.id} className="border-2 border-slate-200 rounded p-4 bg-white" data-testid={`progress-item-${u.id}`}>
          <div className="flex items-center justify-between mb-1">
            <div className="font-bold">{u.milestone}</div>
            {u.percent_complete != null && <span className="px-2 py-0.5 bg-emerald-100 text-emerald-800 rounded text-xs font-bold">{u.percent_complete}%</span>}
          </div>
          <div className="text-xs text-slate-500 mb-2">{u.uploaded_by_name} · {new Date(u.created_at).toLocaleString()}</div>
          {u.notes && <div className="text-sm mb-2 whitespace-pre-wrap">{u.notes}</div>}
          {u.photo_urls?.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {u.photo_urls.map((url, i) => <img key={i} src={url} alt="" className="w-24 h-24 object-cover rounded border-2 border-slate-200" />)}
            </div>
          )}
        </div>
      ))}

      {show && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="border-b-2 border-slate-200 p-4 flex items-center justify-between"><h2 className="font-bold text-lg">New Progress Update</h2><button onClick={() => setShow(false)}><X className="w-5 h-5 text-slate-400" /></button></div>
            <div className="p-4 space-y-3">
              <input value={f.milestone} onChange={e => setF({...f, milestone: e.target.value})} placeholder="Milestone (e.g. Steel arrived on site)" className="w-full border-2 border-slate-200 rounded px-3 py-2" data-testid="progress-milestone" />
              <input type="number" min="0" max="100" value={f.percent_complete} onChange={e => setF({...f, percent_complete: e.target.value})} placeholder="% complete" className="w-full border-2 border-slate-200 rounded px-3 py-2" data-testid="progress-percent" />
              <textarea value={f.notes} onChange={e => setF({...f, notes: e.target.value})} placeholder="Notes…" rows={3} className="w-full border-2 border-slate-200 rounded px-3 py-2" data-testid="progress-notes" />
              <label className="inline-flex items-center gap-2 cursor-pointer px-3 py-2 border-2 border-dashed border-slate-300 hover:border-orange-400 rounded text-sm text-slate-600">
                <Camera className="w-4 h-4" /> Add Photos ({f.photo_urls.length})
                <input type="file" accept="image/*" multiple className="hidden" onChange={addPhoto} data-testid="progress-photos" />
              </label>
              {f.photo_urls.length > 0 && (
                <div className="flex flex-wrap gap-2">{f.photo_urls.map((u, i) => <img key={i} src={u} alt="" className="w-16 h-16 object-cover rounded" />)}</div>
              )}
            </div>
            <div className="border-t-2 border-slate-200 p-4 flex gap-2">
              <button onClick={() => setShow(false)} className="flex-1 px-3 py-2 border-2 border-slate-200 rounded font-semibold">Cancel</button>
              <button onClick={submit} disabled={uploading} className="flex-1 px-3 py-2 bg-orange-500 hover:bg-orange-600 text-white rounded font-bold" data-testid="progress-submit">{uploading ? "Saving…" : "Save"}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ============================================================
const SubbiesTab = ({ job, onSaved }) => {
  const [show, setShow] = useState(false);
  const [f, setF] = useState({ name: "", role: "fabricator", contact_name: "", contact_phone: "", contact_email: "", contract_amount_aud: "", notes: "" });
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!f.name.trim()) return toast.error("Name required");
    setSaving(true);
    try {
      await axios.post(`${API}/jobs/${job.id}/subbies`, { ...f, contract_amount_aud: f.contract_amount_aud ? Number(f.contract_amount_aud) : null });
      toast.success("Subbie added");
      setF({ name: "", role: "fabricator", contact_name: "", contact_phone: "", contact_email: "", contract_amount_aud: "", notes: "" });
      setShow(false);
      await onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };

  const remove = async (sid) => {
    if (!window.confirm("Remove this subbie?")) return;
    try {
      await axios.delete(`${API}/jobs/${job.id}/subbies/${sid}`);
      toast.success("Removed");
      await onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const subbies = job.subbies || [];

  return (
    <div className="space-y-3">
      <button onClick={() => setShow(true)} className="inline-flex gap-2 px-4 py-2 bg-orange-500 hover:bg-orange-600 text-white rounded font-semibold text-sm" data-testid="add-subbie-btn"><Plus className="w-4 h-4" /> Add Subbie</button>
      {subbies.length === 0 ? <div className="text-center py-8 text-slate-400 italic">No subbies added yet.</div> :
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {subbies.map(s => (
            <div key={s.id} className="border-2 border-slate-200 rounded p-3 bg-white" data-testid={`subbie-${s.id}`}>
              <div className="flex justify-between items-start">
                <div>
                  <div className="font-bold">{s.name}</div>
                  <div className="text-xs uppercase text-slate-500 font-semibold">{s.role.replace(/_/g, " ")}</div>
                </div>
                <button onClick={() => remove(s.id)} className="text-red-500 hover:text-red-700 text-xs">Remove</button>
              </div>
              {s.contact_name && <div className="text-xs mt-1">{s.contact_name} · {s.contact_phone || ""} · {s.contact_email || ""}</div>}
              {s.contract_amount_aud && <div className="text-xs font-semibold text-emerald-700 mt-1">${Number(s.contract_amount_aud).toLocaleString()} AUD</div>}
              {s.notes && <div className="text-xs italic text-slate-500 mt-1">{s.notes}</div>}
            </div>
          ))}
        </div>
      }

      {show && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded shadow-2xl w-full max-w-lg">
            <div className="border-b-2 border-slate-200 p-4 flex items-center justify-between"><h2 className="font-bold text-lg">Add Subbie</h2><button onClick={() => setShow(false)}><X className="w-5 h-5 text-slate-400" /></button></div>
            <div className="p-4 space-y-3">
              <div className="grid grid-cols-2 gap-2">
                <input value={f.name} onChange={e => setF({...f, name: e.target.value})} placeholder="Company / crew name" className="border-2 border-slate-200 rounded px-3 py-2" data-testid="subbie-name" />
                <select value={f.role} onChange={e => setF({...f, role: e.target.value})} className="border-2 border-slate-200 rounded px-3 py-2" data-testid="subbie-role">
                  {SUBBIE_ROLES.map(r => <option key={r.id} value={r.id}>{r.label}</option>)}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <input value={f.contact_name} onChange={e => setF({...f, contact_name: e.target.value})} placeholder="Contact name" className="border-2 border-slate-200 rounded px-3 py-2" />
                <input value={f.contact_phone} onChange={e => setF({...f, contact_phone: e.target.value})} placeholder="Phone" className="border-2 border-slate-200 rounded px-3 py-2" />
              </div>
              <input type="email" value={f.contact_email} onChange={e => setF({...f, contact_email: e.target.value})} placeholder="Email" className="w-full border-2 border-slate-200 rounded px-3 py-2" />
              <input type="number" value={f.contract_amount_aud} onChange={e => setF({...f, contract_amount_aud: e.target.value})} placeholder="Contract amount (AUD)" className="w-full border-2 border-slate-200 rounded px-3 py-2" />
              <textarea value={f.notes} onChange={e => setF({...f, notes: e.target.value})} placeholder="Notes…" rows={2} className="w-full border-2 border-slate-200 rounded px-3 py-2" />
            </div>
            <div className="border-t-2 border-slate-200 p-4 flex gap-2">
              <button onClick={() => setShow(false)} className="flex-1 px-3 py-2 border-2 border-slate-200 rounded font-semibold">Cancel</button>
              <button onClick={submit} disabled={saving} className="flex-1 px-3 py-2 bg-orange-500 hover:bg-orange-600 text-white rounded font-bold" data-testid="subbie-submit">{saving ? "Saving…" : "Add"}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ============================================================
const DeliveriesTab = ({ job, onSaved }) => {
  const [show, setShow] = useState(false);
  const [f, setF] = useState({ description: "", scheduled_date: new Date().toISOString().slice(0, 10), quantity_note: "", transport_subbie_name: "" });
  const [arriveFor, setArriveFor] = useState(null);
  const [arrivePhotos, setArrivePhotos] = useState([]);
  const [arriveNotes, setArriveNotes] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!f.description.trim()) return toast.error("Description required");
    setSaving(true);
    try {
      await axios.post(`${API}/jobs/${job.id}/deliveries`, f);
      toast.success("Delivery scheduled");
      setF({ description: "", scheduled_date: new Date().toISOString().slice(0, 10), quantity_note: "", transport_subbie_name: "" });
      setShow(false);
      await onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };

  const submitArrival = async () => {
    try {
      await axios.post(`${API}/jobs/${job.id}/deliveries/${arriveFor.id}/arrived`, { arrival_photos: arrivePhotos, arrival_notes: arriveNotes });
      toast.success("Marked arrived");
      setArriveFor(null); setArrivePhotos([]); setArriveNotes("");
      await onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const uploadArrivePhotos = async (e) => {
    const files = Array.from(e.target.files || []);
    const urls = [];
    for (const file of files) urls.push(await fileToBase64(file));
    setArrivePhotos([...arrivePhotos, ...urls]);
  };

  const deliveries = job.deliveries || [];
  return (
    <div className="space-y-3">
      <button onClick={() => setShow(true)} className="inline-flex gap-2 px-4 py-2 bg-orange-500 hover:bg-orange-600 text-white rounded font-semibold text-sm" data-testid="add-delivery-btn"><Plus className="w-4 h-4" /> Schedule Delivery</button>
      {deliveries.length === 0 ? <div className="text-center py-8 text-slate-400 italic">No deliveries scheduled yet.</div> :
        deliveries.map(d => (
          <div key={d.id} className="border-2 border-slate-200 rounded p-4 bg-white" data-testid={`delivery-${d.id}`}>
            <div className="flex justify-between items-start mb-1">
              <div>
                <div className="font-bold">{d.description}</div>
                <div className="text-xs text-slate-500"><Calendar className="w-3 h-3 inline mr-1" />{d.scheduled_date} · {d.quantity_note || ""}</div>
                {d.transport_subbie_name && <div className="text-xs text-slate-600"><Truck className="w-3 h-3 inline mr-1" />{d.transport_subbie_name}</div>}
              </div>
              <span className={`px-2 py-0.5 rounded text-[10px] uppercase font-bold ${d.status === "arrived" ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800"}`}>{d.status}</span>
            </div>
            {d.status !== "arrived" && <button onClick={() => setArriveFor(d)} className="mt-2 px-3 py-1 bg-emerald-600 hover:bg-emerald-700 text-white text-xs rounded font-semibold" data-testid={`delivery-arrive-${d.id}`}>Mark Arrived</button>}
            {d.arrival_photos?.length > 0 && <div className="flex flex-wrap gap-2 mt-2">{d.arrival_photos.map((u, i) => <img key={i} src={u} alt="" className="w-20 h-20 object-cover rounded border-2 border-slate-200" />)}</div>}
            {d.arrival_notes && <div className="text-xs italic text-slate-500 mt-1">{d.arrival_notes}</div>}
          </div>
        ))
      }

      {show && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded shadow-2xl w-full max-w-lg">
            <div className="border-b-2 border-slate-200 p-4 flex items-center justify-between"><h2 className="font-bold text-lg">Schedule Delivery</h2><button onClick={() => setShow(false)}><X className="w-5 h-5 text-slate-400" /></button></div>
            <div className="p-4 space-y-3">
              <input value={f.description} onChange={e => setF({...f, description: e.target.value})} placeholder="Description (e.g. Columns batch 1)" className="w-full border-2 border-slate-200 rounded px-3 py-2" data-testid="del-desc" />
              <input type="date" value={f.scheduled_date} onChange={e => setF({...f, scheduled_date: e.target.value})} className="w-full border-2 border-slate-200 rounded px-3 py-2" />
              <input value={f.quantity_note} onChange={e => setF({...f, quantity_note: e.target.value})} placeholder="Quantity note (e.g. 12 columns, 3 trailers)" className="w-full border-2 border-slate-200 rounded px-3 py-2" />
              <input value={f.transport_subbie_name} onChange={e => setF({...f, transport_subbie_name: e.target.value})} placeholder="Transport subbie name" className="w-full border-2 border-slate-200 rounded px-3 py-2" />
            </div>
            <div className="border-t-2 border-slate-200 p-4 flex gap-2">
              <button onClick={() => setShow(false)} className="flex-1 px-3 py-2 border-2 border-slate-200 rounded font-semibold">Cancel</button>
              <button onClick={submit} disabled={saving} className="flex-1 px-3 py-2 bg-orange-500 hover:bg-orange-600 text-white rounded font-bold" data-testid="del-submit">{saving ? "Saving…" : "Schedule"}</button>
            </div>
          </div>
        </div>
      )}

      {arriveFor && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded shadow-2xl w-full max-w-lg">
            <div className="border-b-2 border-slate-200 p-4 flex items-center justify-between"><h2 className="font-bold text-lg">Mark Arrived — {arriveFor.description}</h2><button onClick={() => setArriveFor(null)}><X className="w-5 h-5 text-slate-400" /></button></div>
            <div className="p-4 space-y-3">
              <label className="inline-flex items-center gap-2 cursor-pointer px-3 py-2 border-2 border-dashed border-slate-300 hover:border-orange-400 rounded text-sm text-slate-600"><Camera className="w-4 h-4" /> Add arrival photos ({arrivePhotos.length})<input type="file" accept="image/*" multiple className="hidden" onChange={uploadArrivePhotos} /></label>
              {arrivePhotos.length > 0 && <div className="flex flex-wrap gap-2">{arrivePhotos.map((u, i) => <img key={i} src={u} alt="" className="w-16 h-16 object-cover rounded" />)}</div>}
              <textarea value={arriveNotes} onChange={e => setArriveNotes(e.target.value)} placeholder="Arrival notes…" rows={2} className="w-full border-2 border-slate-200 rounded px-3 py-2" />
            </div>
            <div className="border-t-2 border-slate-200 p-4 flex gap-2">
              <button onClick={() => setArriveFor(null)} className="flex-1 px-3 py-2 border-2 border-slate-200 rounded font-semibold">Cancel</button>
              <button onClick={submitArrival} className="flex-1 px-3 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded font-bold">Confirm Arrival</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ============================================================
const PaymentsTab = ({ job, onSaved }) => {
  const [show, setShow] = useState(false);
  const [f, setF] = useState({ milestone: "", amount_aud: "", due_date: "", notes: "" });
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!f.milestone.trim() || !f.amount_aud) return toast.error("Milestone and amount required");
    setSaving(true);
    try {
      await axios.post(`${API}/jobs/${job.id}/progress-payments`, { ...f, amount_aud: Number(f.amount_aud) });
      toast.success("Progress payment added");
      setF({ milestone: "", amount_aud: "", due_date: "", notes: "" });
      setShow(false);
      await onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };

  const setStatus = async (pid, action) => {
    try {
      await axios.post(`${API}/jobs/${job.id}/progress-payments/${pid}/${action}`);
      toast.success("Updated");
      await onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const payments = job.progress_payments || [];
  return (
    <div className="space-y-3">
      <button onClick={() => setShow(true)} className="inline-flex gap-2 px-4 py-2 bg-orange-500 hover:bg-orange-600 text-white rounded font-semibold text-sm" data-testid="add-payment-btn"><Plus className="w-4 h-4" /> New Progress Payment</button>
      {payments.length === 0 ? <div className="text-center py-8 text-slate-400 italic">No progress payments planned yet.</div> :
        payments.map(p => (
          <div key={p.id} className="border-2 border-slate-200 rounded p-3 bg-white flex justify-between items-center" data-testid={`payment-${p.id}`}>
            <div>
              <div className="font-bold">{p.milestone}</div>
              <div className="text-xs text-slate-500">${Number(p.amount_aud).toLocaleString()} AUD · Due {p.due_date || "—"}</div>
              {p.notes && <div className="text-xs italic text-slate-500">{p.notes}</div>}
            </div>
            <div className="flex items-center gap-2">
              <span className={`px-2 py-0.5 rounded text-[10px] uppercase font-bold ${p.status === "paid" ? "bg-emerald-100 text-emerald-800" : p.status === "invoiced" ? "bg-blue-100 text-blue-800" : "bg-slate-100 text-slate-600"}`}>{p.status}</span>
              {p.status === "planned" && <button onClick={() => setStatus(p.id, "mark-invoiced")} className="text-xs px-2 py-1 bg-blue-600 text-white rounded font-semibold" data-testid={`invoice-${p.id}`}>Mark Invoiced</button>}
              {p.status === "invoiced" && <button onClick={() => setStatus(p.id, "mark-paid")} className="text-xs px-2 py-1 bg-emerald-600 text-white rounded font-semibold" data-testid={`paid-${p.id}`}>Mark Paid</button>}
            </div>
          </div>
        ))
      }

      {show && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded shadow-2xl w-full max-w-md">
            <div className="border-b-2 border-slate-200 p-4 flex items-center justify-between"><h2 className="font-bold text-lg">New Progress Payment</h2><button onClick={() => setShow(false)}><X className="w-5 h-5 text-slate-400" /></button></div>
            <div className="p-4 space-y-3">
              <input value={f.milestone} onChange={e => setF({...f, milestone: e.target.value})} placeholder="Milestone (e.g. 30% erected)" className="w-full border-2 border-slate-200 rounded px-3 py-2" data-testid="pp-milestone" />
              <input type="number" value={f.amount_aud} onChange={e => setF({...f, amount_aud: e.target.value})} placeholder="Amount (AUD)" className="w-full border-2 border-slate-200 rounded px-3 py-2" data-testid="pp-amount" />
              <input type="date" value={f.due_date} onChange={e => setF({...f, due_date: e.target.value})} className="w-full border-2 border-slate-200 rounded px-3 py-2" />
              <textarea value={f.notes} onChange={e => setF({...f, notes: e.target.value})} placeholder="Notes…" rows={2} className="w-full border-2 border-slate-200 rounded px-3 py-2" />
            </div>
            <div className="border-t-2 border-slate-200 p-4 flex gap-2">
              <button onClick={() => setShow(false)} className="flex-1 px-3 py-2 border-2 border-slate-200 rounded font-semibold">Cancel</button>
              <button onClick={submit} disabled={saving} className="flex-1 px-3 py-2 bg-orange-500 hover:bg-orange-600 text-white rounded font-bold" data-testid="pp-submit">{saving ? "Saving…" : "Save"}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ============================================================
const ReceiptsTab = ({ jobId, receipts, onSaved }) => {
  const [show, setShow] = useState(false);
  const [f, setF] = useState({ amount_aud: "", supplier: "", description: "", category: "materials", photo_url: "", receipt_date: new Date().toISOString().slice(0, 10) });
  const [saving, setSaving] = useState(false);

  const uploadPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (file) setF({ ...f, photo_url: await fileToBase64(file) });
  };

  const submit = async () => {
    if (!f.amount_aud || !f.supplier.trim()) return toast.error("Amount and supplier required");
    setSaving(true);
    try {
      await axios.post(`${API}/jobs/${jobId}/receipts`, { ...f, amount_aud: Number(f.amount_aud) });
      toast.success("Receipt saved");
      setF({ amount_aud: "", supplier: "", description: "", category: "materials", photo_url: "", receipt_date: new Date().toISOString().slice(0, 10) });
      setShow(false);
      await onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this receipt?")) return;
    try {
      await axios.delete(`${API}/jobs/${jobId}/receipts/${id}`);
      toast.success("Deleted");
      await onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="space-y-3">
      <button onClick={() => setShow(true)} className="inline-flex gap-2 px-4 py-2 bg-orange-500 hover:bg-orange-600 text-white rounded font-semibold text-sm" data-testid="add-receipt-btn"><Plus className="w-4 h-4" /> Upload Receipt / Docket</button>
      {receipts.length === 0 ? <div className="text-center py-8 text-slate-400 italic">No receipts uploaded yet.</div> :
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {receipts.map(r => (
            <div key={r.id} className="border-2 border-slate-200 rounded p-3 bg-white" data-testid={`receipt-${r.id}`}>
              <div className="flex justify-between items-start">
                <div>
                  <div className="font-bold">${Number(r.amount_aud).toLocaleString()} — {r.supplier}</div>
                  <div className="text-xs text-slate-500">{r.receipt_date} · {r.category || "—"}</div>
                  {r.description && <div className="text-xs mt-1">{r.description}</div>}
                  <div className="text-[10px] text-slate-400 mt-1">by {r.uploaded_by_name}</div>
                </div>
                <button onClick={() => remove(r.id)} className="text-red-500 text-xs">Delete</button>
              </div>
              {r.photo_url && <img src={r.photo_url} alt="" className="mt-2 w-full max-h-48 object-contain rounded border border-slate-200" />}
            </div>
          ))}
        </div>
      }

      {show && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded shadow-2xl w-full max-w-md max-h-[90vh] overflow-y-auto">
            <div className="border-b-2 border-slate-200 p-4 flex items-center justify-between"><h2 className="font-bold text-lg">Upload Receipt</h2><button onClick={() => setShow(false)}><X className="w-5 h-5 text-slate-400" /></button></div>
            <div className="p-4 space-y-3">
              <div className="grid grid-cols-2 gap-2">
                <input type="number" value={f.amount_aud} onChange={e => setF({...f, amount_aud: e.target.value})} placeholder="Amount (AUD)" className="border-2 border-slate-200 rounded px-3 py-2" data-testid="rc-amount" />
                <input type="date" value={f.receipt_date} onChange={e => setF({...f, receipt_date: e.target.value})} className="border-2 border-slate-200 rounded px-3 py-2" />
              </div>
              <input value={f.supplier} onChange={e => setF({...f, supplier: e.target.value})} placeholder="Supplier" className="w-full border-2 border-slate-200 rounded px-3 py-2" data-testid="rc-supplier" />
              <select value={f.category} onChange={e => setF({...f, category: e.target.value})} className="w-full border-2 border-slate-200 rounded px-3 py-2" data-testid="rc-category">
                <option value="materials">Materials</option>
                <option value="fuel">Fuel</option>
                <option value="hire">Hire</option>
                <option value="subbie">Subbie</option>
                <option value="tools">Tools</option>
                <option value="other">Other</option>
              </select>
              <textarea value={f.description} onChange={e => setF({...f, description: e.target.value})} placeholder="Description…" rows={2} className="w-full border-2 border-slate-200 rounded px-3 py-2" />
              <label className="inline-flex items-center gap-2 cursor-pointer px-3 py-2 border-2 border-dashed border-slate-300 hover:border-orange-400 rounded text-sm text-slate-600"><ImageIcon className="w-4 h-4" /> {f.photo_url ? "Change photo" : "Add photo"}<input type="file" accept="image/*" className="hidden" onChange={uploadPhoto} data-testid="rc-photo" /></label>
              {f.photo_url && <img src={f.photo_url} alt="" className="w-full max-h-40 object-contain rounded border" />}
            </div>
            <div className="border-t-2 border-slate-200 p-4 flex gap-2">
              <button onClick={() => setShow(false)} className="flex-1 px-3 py-2 border-2 border-slate-200 rounded font-semibold">Cancel</button>
              <button onClick={submit} disabled={saving} className="flex-1 px-3 py-2 bg-orange-500 hover:bg-orange-600 text-white rounded font-bold" data-testid="rc-submit">{saving ? "Saving…" : "Save"}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ============================================================
const ReportTab = ({ job, budget, receipts, report }) => {
  const b = budget || {};
  const labourSummary = report?.labour_summary || [];
  const labourTotals = report?.labour_totals || { hours: 0, worker_count: 0, shift_count: 0 };
  const labourEntries = report?.labour_entries || [];
  const fmtDT = (iso) => iso ? new Date(iso).toLocaleString('en-AU', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—';
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <div className="border-2 border-slate-200 rounded p-3 bg-white"><div className="text-[10px] uppercase font-bold text-slate-500">Contract</div><div className="text-xl font-bold text-slate-900">${Number(b.contract_value_aud || 0).toLocaleString()}</div></div>
        <div className="border-2 border-slate-200 rounded p-3 bg-white"><div className="text-[10px] uppercase font-bold text-slate-500">Spent (Receipts)</div><div className="text-xl font-bold text-orange-700">${Number(b.total_receipts_aud || 0).toLocaleString()}</div></div>
        <div className="border-2 border-slate-200 rounded p-3 bg-white"><div className="text-[10px] uppercase font-bold text-slate-500">Labour Hours</div><div className="text-xl font-bold text-blue-700">{Number(b.labour_hours_total || 0).toLocaleString()}</div><div className="text-[10px] text-slate-500">{b.labour_shifts_count || 0} shift(s)</div></div>
        <div className="border-2 border-slate-200 rounded p-3 bg-white"><div className="text-[10px] uppercase font-bold text-slate-500">Subbie Contracts</div><div className="text-xl font-bold text-purple-700">${Number(b.subbie_contracts_aud || 0).toLocaleString()}</div></div>
        <div className={`border-2 rounded p-3 ${(b.estimated_gross_margin_aud || 0) >= 0 ? "border-emerald-300 bg-emerald-50" : "border-red-300 bg-red-50"}`}><div className="text-[10px] uppercase font-bold text-slate-500">Est. Margin</div><div className={`text-xl font-bold ${(b.estimated_gross_margin_aud || 0) >= 0 ? "text-emerald-700" : "text-red-700"}`}>${Number(b.estimated_gross_margin_aud || 0).toLocaleString()}</div><div className="text-[10px] text-slate-500">{b.estimated_margin_pct != null ? `${b.estimated_margin_pct.toFixed(1)}%` : "—"}</div></div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="border-2 border-slate-200 rounded p-4 bg-white">
          <h3 className="font-bold uppercase text-xs text-slate-500 mb-2">Payments</h3>
          <div className="space-y-1 text-sm">
            <div>Invoiced: <span className="font-bold">${Number(b.progress_invoiced_aud || 0).toLocaleString()}</span></div>
            <div>Paid: <span className="font-bold text-emerald-700">${Number(b.progress_paid_aud || 0).toLocaleString()}</span></div>
            <div>Outstanding: <span className="font-bold text-amber-700">${Number(b.outstanding_aud || 0).toLocaleString()}</span></div>
          </div>
        </div>
        <div className="border-2 border-slate-200 rounded p-4 bg-white">
          <h3 className="font-bold uppercase text-xs text-slate-500 mb-2">Receipts by Category</h3>
          <div className="space-y-1 text-sm">
            {Object.entries(b.receipts_by_category || {}).length === 0 ? <div className="text-slate-400 italic">No receipts yet</div> :
              Object.entries(b.receipts_by_category || {}).map(([k, v]) => (
                <div key={k} className="flex justify-between"><span className="capitalize">{k}</span><span className="font-semibold">${Number(v).toLocaleString()}</span></div>
              ))}
          </div>
        </div>
      </div>

      {/* Labour — who was on site & when (from clock-ons tagged to this job) */}
      <div className="border-2 border-slate-200 rounded p-4 bg-white" data-testid="labour-section">
        <div className="flex justify-between items-center mb-2">
          <h3 className="font-bold uppercase text-xs text-slate-500">Labour — Who was on site & when</h3>
          <div className="text-xs text-slate-500">
            {labourTotals.worker_count} worker(s) · {labourTotals.shift_count} shift(s) · <span className="font-bold">{Number(labourTotals.hours).toLocaleString()} hrs</span>
          </div>
        </div>
        {labourSummary.length === 0 ? (
          <div className="text-sm text-slate-400 italic py-2">No clock-ons for this job yet.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[10px] uppercase text-slate-500 border-b border-slate-200">
                  <th className="py-2 pr-2">Worker</th>
                  <th className="py-2 pr-2">Role</th>
                  <th className="py-2 pr-2 text-right">Shifts</th>
                  <th className="py-2 pr-2 text-right">Hours</th>
                  <th className="py-2 pr-2">First on site</th>
                  <th className="py-2 pr-2">Last on site</th>
                </tr>
              </thead>
              <tbody>
                {labourSummary.map(w => (
                  <tr key={w.user_id} className="border-b border-slate-100" data-testid={`labour-worker-${w.user_id}`}>
                    <td className="py-2 pr-2 font-semibold">{w.user_name}</td>
                    <td className="py-2 pr-2 uppercase text-[10px] text-slate-500">{(w.role || '').replace(/_/g, ' ')}</td>
                    <td className="py-2 pr-2 text-right">{w.shifts}</td>
                    <td className="py-2 pr-2 text-right font-bold">{Number(w.total_hours).toLocaleString()}</td>
                    <td className="py-2 pr-2 text-xs text-slate-500">{fmtDT(w.first_on_site)}</td>
                    <td className="py-2 pr-2 text-xs text-slate-500">{fmtDT(w.last_on_site)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Individual shifts log */}
      {labourEntries.length > 0 && (
        <div className="border-2 border-slate-200 rounded p-4 bg-white">
          <h3 className="font-bold uppercase text-xs text-slate-500 mb-2">Every shift on this job</h3>
          <div className="overflow-x-auto max-h-96 overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-white">
                <tr className="text-left text-[10px] uppercase text-slate-500 border-b border-slate-200">
                  <th className="py-2 pr-2">Worker</th>
                  <th className="py-2 pr-2">Clock In</th>
                  <th className="py-2 pr-2">Clock Out</th>
                  <th className="py-2 pr-2 text-right">Hours</th>
                </tr>
              </thead>
              <tbody>
                {labourEntries.map(e => (
                  <tr key={e.id} className="border-b border-slate-100" data-testid={`labour-entry-${e.id}`}>
                    <td className="py-1.5 pr-2 font-medium">{e.user_name}</td>
                    <td className="py-1.5 pr-2 text-xs">{fmtDT(e.clock_in)}</td>
                    <td className="py-1.5 pr-2 text-xs">{e.is_open ? <span className="text-emerald-600 font-bold">Still on site</span> : fmtDT(e.clock_out)}</td>
                    <td className="py-1.5 pr-2 text-right">{Number(e.hours).toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="border-2 border-slate-200 rounded p-4 bg-white">
        <h3 className="font-bold uppercase text-xs text-slate-500 mb-2">Job Summary</h3>
        <div className="grid grid-cols-2 gap-2 text-sm">
          <div>Type: <span className="font-semibold uppercase">{job.job_type?.replace(/_/g, " ") || "—"}</span></div>
          <div>Tonnage: <span className="font-semibold">{job.tonnage ?? "—"}</span></div>
          <div>Builder: <span className="font-semibold">{job.builder_name || "—"}</span></div>
          <div>Fabricator: <span className="font-semibold">{job.fabricator_name || "—"}</span></div>
          <div>Progress: <span className="font-semibold">{job.percent_complete ?? "—"}%</span></div>
          <div>Subbies: <span className="font-semibold">{(job.subbies || []).length}</span></div>
          <div>Progress updates: <span className="font-semibold">{(job.progress_updates || []).length}</span></div>
          <div>Receipts: <span className="font-semibold">{receipts.length}</span></div>
        </div>
      </div>
    </div>
  );
};

export default BuildWorkflowPage;
